import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import type { LucideIcon } from "lucide-react";

interface CanvasActionButtonProps {
  icon: LucideIcon;
  /** Names the button for screen readers and fills the tooltip. */
  label: string;
  /** Longer explanation when the label alone doesn't justify the action. */
  hint?: string;
  onClick: () => void;
  /** Bar-sized (32px) instead of canvas-sized (40px). */
  compact?: boolean;
}

/**
 * The one look for icon-only assistant controls — floating over the canvas on
 * desktop, sitting in the top bar on small screens. Shared so New chat and
 * Temporary chat cannot drift into two different buttons that do similar jobs.
 */
export const CanvasActionButton = ({
  icon: Icon,
  label,
  hint,
  onClick,
  compact = false,
}: CanvasActionButtonProps) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={label}
          onClick={onClick}
          className={cn(
            "surface-frosted text-muted-foreground hover:text-foreground border-border rounded-full border shadow-sm",
            compact ? "size-8 [&_svg]:size-4" : "size-10 [&_svg]:size-5",
          )}
        >
          <Icon />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-56 text-xs">
        {hint ?? label}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
